/// Port of mobile/lib/game/dino_dash.dart.
///
/// Flame's CameraComponent.withFixedResolution(600, 150) has no web
/// counterpart because none is needed: the canvas backing store is exactly
/// 600x150 and CSS scales it, so world coordinates are canvas coordinates.

import { appColors } from '@/app/theme/appColors';
import {
  defaultKeyboardBindings,
  keysEqual,
  type KeyboardBindings,
} from '@/settings/data/keyBindings';

import type { AudioManager } from './audio/audioManager';
import { Component } from './engine/component';
import { collides } from './engine/collision';
import type { Rng } from './engine/random';
import { Dino } from './dino';
import { deathCauseFromEnemy, type DeathCauseApi } from './domain/deathCause';
import { Enemy } from './enemy';
import { EnemyManager } from './enemyManager';
import type { GameEvent } from './gameEvent';
import { GameEventEmitter } from './gameEvent';
import {
  baseSpeedToPixelsPerSecond,
  fixedTimeStep,
  initialBaseSpeed,
  maxAccumulator,
  scoreInterval,
  stepBaseSpeed,
} from './gamePhysics';
import type { GameState } from './gameState';
import { createGameState, isDead } from './gameState';
import { Ground } from './ground';
import { HighScoreLabel } from './highScoreLabel';
import { DaySky } from './sky/daySky';
import { NightSky } from './sky/nightSky';
import { worldHeight, worldWidth } from './world';

export interface DinoDashOptions {
  canvas: HTMLCanvasElement;
  audio: AudioManager;
  isDarkMode?: boolean;
  highScore?: number;
  rng?: Rng;
}

export class DinoDash {
  readonly events = new GameEventEmitter();

  gameState: GameState;
  isDarkMode: boolean;

  readonly world = new Component();
  readonly ground: Ground;
  readonly dino: Dino;
  readonly enemyManager: EnemyManager;
  readonly highScoreLabel: HighScoreLabel;
  readonly nightSky: NightSky;
  readonly daySky: DaySky;

  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private readonly audio: AudioManager;

  /// Internal Chrome-style speed (~ px/frame at 60 Hz). Prefer currentSpeed.
  private baseSpeed = initialBaseSpeed;
  private accumulator = 0.0;

  private rafHandle: number | null = null;
  private lastFrameTime = 0;
  private enginePaused = false;

  runJumpCount = 0;
  runDuckCount = 0;
  runDeathCause: DeathCauseApi | null = null;
  private runIsDucking = false;

  constructor(options: DinoDashOptions) {
    this.canvas = options.canvas;
    this.audio = options.audio;
    this.isDarkMode = options.isDarkMode ?? true;
    this.gameState = createGameState({ highScore: options.highScore ?? 0 });

    const context = this.canvas.getContext('2d', { alpha: false });
    if (!context) throw new Error('dinoDash: 2d context unavailable');
    this.ctx = context;

    this.canvas.width = worldWidth;
    this.canvas.height = worldHeight;

    const isPlaying = (): boolean => this.isPlaying;
    const currentSpeed = (): number => this.currentSpeed;
    const allowsAmbientMotion = (): boolean => this.allowsAmbientMotion;

    this.ground = new Ground(this.isDarkMode, isPlaying, currentSpeed);
    this.world.add(this.ground);

    this.dino = new Dino(this.isDarkMode);
    this.world.add(this.dino);

    this.enemyManager = new EnemyManager({
      isPlaying,
      currentSpeed,
      baseSpeed: () => this.baseSpeed,
      isDarkMode: () => this.isDarkMode,
      world: this.world,
      rng: options.rng,
    });
    this.world.add(this.enemyManager);

    this.nightSky = new NightSky({
      allowsAmbientMotion,
      isPlaying,
      currentScore: () => this.gameState.currentScore,
      rng: options.rng,
    });
    this.daySky = new DaySky({ allowsAmbientMotion, currentSpeed, rng: options.rng });
    this.world.add(this.isDarkMode ? this.nightSky : this.daySky);

    this.highScoreLabel = new HighScoreLabel(
      {
        isWaiting: () => this.gameState.status === 'waiting',
        isDarkMode: () => this.isDarkMode,
        dinoPosition: () => this.dino.position,
        dinoSize: () => this.dino.size,
      },
      this.gameState.highScore,
    );
    this.world.add(this.highScoreLabel);

    this.world.processLifecycle();
    this.audio.startBgm();
  }

  get backgroundColor(): string {
    return this.isDarkMode ? appColors.darkSurface : appColors.lightSurface;
  }

  /// World scroll / obstacle speed in px/s. Single source via baseSpeed.
  get currentSpeed(): number {
    return baseSpeedToPixelsPerSecond(this.baseSpeed);
  }

  get isPlaying(): boolean {
    return this.gameState.status === 'playing';
  }

  /// Ambient sky / decorative motion (home + in-run). Frozen on game over.
  get allowsAmbientMotion(): boolean {
    return this.gameState.status === 'waiting' || this.gameState.status === 'playing';
  }

  /// Hot-swaps light/dark art without restarting the run.
  setDarkMode(value: boolean): void {
    if (this.isDarkMode === value) return;
    this.isDarkMode = value;

    this.dino.reloadThemeAssets(value);
    this.ground.reloadThemeAssets(value);
    this.enemyManager.reloadThemeAssets();
    this.syncSky();
  }

  private syncSky(): void {
    if (this.isDarkMode) {
      this.daySky.removeFromParent();
      if (this.nightSky.parent === null) this.world.add(this.nightSky);
    } else {
      this.nightSky.removeFromParent();
      if (this.daySky.parent === null) this.world.add(this.daySky);
    }
  }

  // --- loop ---

  start(): void {
    if (this.rafHandle !== null) return;
    this.lastFrameTime = performance.now();
    this.rafHandle = requestAnimationFrame(this.frame);
  }

  stop(): void {
    if (this.rafHandle === null) return;
    cancelAnimationFrame(this.rafHandle);
    this.rafHandle = null;
  }

  dispose(): void {
    this.stop();
    this.events.close();
  }

  private readonly frame = (now: number): void => {
    this.rafHandle = requestAnimationFrame(this.frame);

    const dt = (now - this.lastFrameTime) / 1000;
    this.lastFrameTime = now;

    // pauseEngine equivalent: keep painting, stop advancing time.
    if (!this.enginePaused) {
      this.update(dt);
    }
    this.render();
  };

  private update(dt: number): void {
    this.world.processLifecycle();

    if (this.isPlaying) {
      this.accumulator += dt;
      if (this.accumulator > maxAccumulator) {
        this.accumulator = maxAccumulator;
      }

      while (this.accumulator >= fixedTimeStep) {
        this.stepGameplay(fixedTimeStep);
        this.accumulator -= fixedTimeStep;
      }
    } else {
      this.accumulator = 0;
    }

    // Animations / non-physics component updates stay on the display clock.
    this.world.updateTree(dt);

    this.runCollisionDetection();
  }

  /// Fixed-timestep gameplay: speed, dino, ground, enemies, spawns, score.
  private stepGameplay(dt: number): void {
    this.baseSpeed = stepBaseSpeed(this.baseSpeed, dt);

    this.dino.stepPhysics(dt);
    this.ground.stepPhysics(dt);
    this.enemyManager.stepPhysics(dt);
    for (const enemy of this.enemyManager.liveEnemies) {
      enemy.stepPhysics(dt);
    }
    this.enemyManager.pruneRemoved();

    const previousScore = this.gameState.currentScore;
    const newDistance = this.gameState.distanceTraveled + this.currentSpeed * dt;
    const newScore = Math.floor(newDistance / scoreInterval);

    this.gameState = {
      ...this.gameState,
      distanceTraveled: newDistance,
      currentScore: newScore,
      highScore: newScore > this.gameState.highScore ? newScore : this.gameState.highScore,
    };

    if (newScore !== previousScore) {
      this.emitEvent('scoreUpdated');
    }
  }

  /// Flame runs collision once per frame after the component tree updates,
  /// not once per fixed step. Matched here so the hit window is the same.
  private runCollisionDetection(): void {
    if (!this.isPlaying || this.dino.isHit) return;

    for (const enemy of this.enemyManager.liveEnemies) {
      if (collides(this.dino, enemy)) {
        this.handleCollision(enemy);
        return;
      }
    }
  }

  private resetRunStats(): void {
    this.runJumpCount = 0;
    this.runDuckCount = 0;
    this.runDeathCause = null;
    this.runIsDucking = false;
  }

  setDuck(isDucking: boolean): void {
    if (this.isPlaying && isDucking && !this.runIsDucking) {
      this.runDuckCount++;
    }
    this.runIsDucking = isDucking;
    this.dino.duck(isDucking);
  }

  private render(): void {
    const ctx = this.ctx;
    ctx.imageSmoothingEnabled = false;
    ctx.fillStyle = this.backgroundColor;
    ctx.fillRect(0, 0, worldWidth, worldHeight);
    this.world.renderTree(ctx, 0, 0);
  }

  // --- state transitions ---

  startGame(): void {
    if (this.gameState.status !== 'waiting') return;

    this.gameState = { ...this.gameState, status: 'playing' };
    this.highScoreLabel.removeFromParent();
    this.dino.startRunning();
    this.audio.resumeBgm();
    this.emitEvent('gameStarted');
  }

  pauseGame(): void {
    if (!this.isPlaying) return;

    this.gameState = { ...this.gameState, status: 'paused' };
    this.enginePaused = true;
    this.audio.pauseBgm();
    this.emitEvent('gamePaused');
  }

  resumeGame(): void {
    if (this.gameState.status !== 'paused') return;

    this.gameState = { ...this.gameState, status: 'playing' };
    this.enginePaused = false;
    // Avoids one giant dt covering the whole pause.
    this.lastFrameTime = performance.now();
    this.audio.resumeBgm();
    this.emitEvent('gameResumed');
  }

  endGame(): void {
    this.gameState = { ...this.gameState, status: 'gameOver' };
    this.dino.hit();
    this.audio.playDie();
    this.audio.pauseBgm();
    this.emitEvent('gameOver');
  }

  retryGame(): void {
    this.resetAll();
    this.gameState = { ...this.gameState, status: 'playing' };
    this.dino.startRunning();
    this.audio.resumeBgm();
    this.emitEvent('gameStarted');
  }

  goToHome(): void {
    this.resetAll();
    this.ensureHighScoreLabel();
    this.audio.resumeBgm();
    this.emitEvent('returnedHome');
  }

  handleCollision(enemy: Enemy): void {
    if (!this.isPlaying) return;

    this.runDeathCause = deathCauseFromEnemy(enemy.enemyData);
    this.gameState = { ...this.gameState, lives: this.gameState.lives - 1 };
    this.emitEvent('dinoCollision');

    if (isDead(this.gameState)) {
      this.endGame();
    }
  }

  requestJump(): void {
    if (!this.isPlaying || this.dino.isJumping || this.dino.isHit) return;
    this.dino.jump();
    this.runJumpCount++;
    this.audio.playJump();
  }

  syncHighScore(score: number): void {
    if (score !== this.gameState.highScore) {
      this.gameState = { ...this.gameState, highScore: score };
    }
    this.highScoreLabel.setScore(this.gameState.highScore);
  }

  private ensureHighScoreLabel(): void {
    this.highScoreLabel.setScore(this.gameState.highScore);
    if (this.highScoreLabel.parent === null) {
      this.world.add(this.highScoreLabel);
    }
  }

  private resetAll(): void {
    this.resetRunStats();
    this.gameState = createGameState({ highScore: this.gameState.highScore });
    this.baseSpeed = initialBaseSpeed;
    this.accumulator = 0;
    this.enginePaused = false;
    this.dino.reset();
    this.enemyManager.reset();
    this.ground.reset();
    if (this.isDarkMode && this.nightSky.isMounted) {
      this.nightSky.reset();
    } else if (!this.isDarkMode && this.daySky.isMounted) {
      this.daySky.reset();
    }
  }

  private emitEvent(event: GameEvent): void {
    this.events.emit(event);
  }

  // --- keyboard ---

  /// Returns true when the key was consumed, so the caller can preventDefault.
  handleKeyDown(key: string, bindings: KeyboardBindings = defaultKeyboardBindings): boolean {
    const jump = keysEqual(key, bindings.jump);
    const duck = keysEqual(key, bindings.duck);
    const action = jump || duck;

    switch (this.gameState.status) {
      case 'waiting':
        if (action) {
          this.startGame();
          return true;
        }
        return false;

      case 'playing':
        if (jump) {
          this.requestJump();
          return true;
        }
        if (duck) {
          this.setDuck(true);
          return true;
        }
        return false;

      case 'paused':
        if (action) {
          this.resumeGame();
          return true;
        }
        return false;

      case 'gameOver':
        if (action) {
          this.retryGame();
          return true;
        }
        return false;
    }
  }

  handleKeyUp(key: string, bindings: KeyboardBindings = defaultKeyboardBindings): boolean {
    if (this.gameState.status === 'playing' && keysEqual(key, bindings.duck)) {
      this.setDuck(false);
      return true;
    }
    return false;
  }
}
